"use client";

import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "~/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { Textarea } from "~/components/ui/textarea";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import * as Analytics from "../../analytics";

// // // //

/** Minimum number of characters required before feedback can be submitted. */
const MIN_FEEDBACK_LENGTH = 10;

/**
 * FeedbackButton
 *
 * "Give feedback" control in the GlobalChatDock's header, next to the
 * maximize button. Opens a popover with a free-text field; submitting sends
 * the text to PostHog as an `Asset Chat - Submit Feedback` event property.
 */
export function FeedbackButton(props: {
    /** The dock view the feedback is given from (list / picker / chat). */
    view: string;
    /** The asset being chatted about, when the dock is showing a chat. */
    assetCorpusId?: string;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [feedback, setFeedback] = useState("");
    const [hasBlurred, setHasBlurred] = useState(false);
    const { addToaster } = useToaster();

    const handleOpenChange = (open: boolean) => {
        setIsOpen(open);
        if (open) {
            Analytics.trackOpenFeedbackPopover({ view: props.view });
        }
    };

    const trimmedFeedback = feedback.trim();
    const canSubmit = trimmedFeedback.length >= MIN_FEEDBACK_LENGTH;
    // Only nudge about the minimum once the user has typed something, come up
    // short, and left the field.
    const showMinLengthHint =
        hasBlurred && trimmedFeedback.length > 0 && !canSubmit;

    const handleSubmit = () => {
        if (!canSubmit) {
            return;
        }
        const trimmed = trimmedFeedback;
        Analytics.trackSubmitFeedback({
            feedback: trimmed,
            view: props.view,
            assetCorpusId: props.assetCorpusId,
        });
        setFeedback("");
        setIsOpen(false);
        addToaster({ message: "Thanks for your feedback!" });
    };

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            {/* Gray twin of the screener's data-room pill (dataRoomPillClassName
                in AssetResultRow.tsx) — same shape, neutral colors. */}
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className="inline-flex h-6 cursor-pointer items-center rounded-md border border-border bg-card px-2.5 text-[11px] font-medium leading-none whitespace-nowrap text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                >
                    Give Feedback
                </button>
            </PopoverTrigger>
            {/* z-[120] so the portal renders above the dock panel (z-[110]). */}
            <PopoverContent
                align="end"
                sideOffset={8}
                className="z-[120] w-80"
                // Consume Escape (dismiss manually) so the dock's own Escape
                // handler doesn't also minimize/close the window.
                onEscapeKeyDown={(event) => {
                    event.preventDefault();
                    setIsOpen(false);
                }}
            >
                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}
                    className="flex flex-col gap-3"
                >
                    <div>
                        <p className="text-sm font-semibold text-foreground">
                            Give feedback
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                            Tell us what&apos;s working — or what isn&apos;t.
                        </p>
                    </div>
                    <Textarea
                        value={feedback}
                        onChange={(event) => setFeedback(event.target.value)}
                        onFocus={() => setHasBlurred(false)}
                        onBlur={() => setHasBlurred(true)}
                        placeholder="Share your thoughts about the AI chat…"
                        rows={4}
                        autoFocus
                        className="resize-none"
                    />
                    <div className="flex items-center justify-between">
                        <p className="text-[11px] text-muted-foreground">
                            {showMinLengthHint
                                ? `Minimum ${MIN_FEEDBACK_LENGTH} characters`
                                : ""}
                        </p>
                        <Button
                            type="submit"
                            size="sm"
                            disabled={!canSubmit}
                            className="h-8 gap-2 rounded-lg px-3 text-xs font-semibold"
                        >
                            <FontAwesomeIcon
                                className="h-4 w-4"
                                icon={faPaperPlane}
                            />
                            Send
                        </Button>
                    </div>
                </form>
            </PopoverContent>
        </Popover>
    );
}
